import { Button, VStack } from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { FormEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  EMPTY_EVALUATED_EMPLOYEE,
  EvaluatedEmployeeFields,
  evaluatedEmployeeErrors,
  evaluatedEmployeePayload,
  IEvaluatedEmployeeForm,
  TEvaluatedEmployeeErrors,
  withoutError,
  withoutErrorsOf
} from '../../../components/EvaluatedEmployeeFields'
import { FormField } from '../../../components/FormField'
import PublicFormLayout from '../../../components/publicFormLayout/PublicFormLayout'
import { FormSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import BrilhaMagentaService from '../../../services/BrilhaMagenta/BrilhaMagentaService'
import { IUser, UserService } from '../../../services/User/UserService'

type ApiError = AxiosError<{ errors?: TEvaluatedEmployeeErrors }>

// Acelera and Conexão also ask the nickname and the birthdate.
const ASKS_PERSONAL = false

const FEEDBACK_TYPE_OPTIONS = [
  { value: 'desenvolvimento', label: 'Desenvolvimento' },
  { value: 'reconhecimento', label: 'Reconhecimento' }
]

export default function BrilhaMagentaNewPage() {
  const toast = useAppToast()
  const navigate = useNavigate()
  const [employeeForm, setEmployeeForm] = useState<IEvaluatedEmployeeForm>(EMPTY_EVALUATED_EMPLOYEE)
  const [feedbackType, setFeedbackType] = useState('')
  const [managerId, setManagerId] = useState('')
  const [managers, setManagers] = useState<IUser[]>([])
  const [errors, setErrors] = useState<TEvaluatedEmployeeErrors>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    new UserService()
      .getManagers()
      .then(setManagers)
      .catch((error) => toast.error('Erro ao carregar os gestores', error))
  }, [])

  const updateEmployee = (patch: Partial<IEvaluatedEmployeeForm>) => {
    setEmployeeForm((current) => ({ ...current, ...patch }))
    setErrors((current) => withoutErrorsOf(current, patch))
  }

  const dropError = (key: string) => setErrors((current) => withoutError(current, key))

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const formErrors = evaluatedEmployeeErrors(employeeForm, ASKS_PERSONAL)
    if (!feedbackType) formErrors.feedback_type = ['Selecione o tipo de feedback.']
    if (!managerId) formErrors.manager_id = ['Selecione o(a) gestor(a) imediato(a).']
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors)
      toast.invalidForm()
      return
    }

    setSaving(true)
    try {
      const response = await new BrilhaMagentaService().store({
        ...evaluatedEmployeePayload(employeeForm, ASKS_PERSONAL),
        feedback_type: feedbackType,
        manager_id: Number(managerId)
      })
      navigate(`/brilha-magenta/${response.data.id}`)
    } catch (error) {
      const apiErrors = (error as ApiError).response?.data?.errors
      if (apiErrors) {
        setErrors(apiErrors)
        toast.invalidForm()
        return
      }
      toast.error('Erro ao iniciar o Brilha Magenta', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <PublicFormLayout title="Brilha Magenta">
      <form onSubmit={handleSubmit} noValidate>
        <VStack spacing="4">
          <EvaluatedEmployeeFields
            value={employeeForm}
            onChange={updateEmployee}
            errors={errors}
            asksPersonal={ASKS_PERSONAL}
          />
          <FormField label="Tipo de feedback" error={errors.feedback_type} isRequired>
            <FormSelect
              value={feedbackType}
              onChange={(value) => {
                setFeedbackType(value)
                dropError('feedback_type')
              }}
              options={FEEDBACK_TYPE_OPTIONS}
              placeholder="Selecione um tipo de feedback"
            />
          </FormField>
          <FormField label="Gestor(a) imediato(a)" error={errors.manager_id} isRequired>
            <FormSelect
              value={managerId}
              onChange={(value) => {
                setManagerId(value)
                dropError('manager_id')
              }}
              options={managers.map((manager) => ({ value: manager.id, label: manager.name }))}
              placeholder="Selecione um gestor"
            />
          </FormField>
          <Button colorScheme="magenta" type="submit" isLoading={saving}>
            Iniciar Brilha Magenta
          </Button>
        </VStack>
      </form>
    </PublicFormLayout>
  )
}
